// src/api/sharepointClient.ts — SCE Issues
// Wrapper fetch authentifié pour SharePoint REST API.
//
// ⚠️ Pourquoi un client séparé de graphClient ?
//   - SharePoint REST a une audience différente de Graph (sartorius.sharepoint.com
//     vs graph.microsoft.com) → token différent.
//   - Certaines opérations (ensureUser pour résoudre un UPN en SP user, par ex.)
//     n'existent pas côté Graph et nécessitent SP REST.
//
// ⚠️ Format des réponses SharePoint REST :
//   - Par défaut, SP REST retourne du XML/ATOM.
//   - On envoie `Accept: application/json;odata=verbose` pour avoir du JSON.
//   - Le format `verbose` enveloppe la réponse dans { d: { ... } } — c'est géré
//     dans les helpers.

import {
  sharepointTokenRequest,
  SHAREPOINT_HOSTNAME,
} from '@/auth/msalConfig'
import { compteConnu, obtenirJeton } from '@/auth/session'
import { suivreRequete } from './activiteReseau'

// ─── Erreur domaine ───

export class SharePointApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'SharePointApiError'
  }
}

// ─── Récupération d'un token SharePoint ───

async function getSharePointAccessToken(): Promise<string> {
  const account = compteConnu()
  if (!account) {
    throw new SharePointApiError('Utilisateur non authentifié', 401)
  }
  // Premier appel : si MSAL n'a pas encore de token SP en cache et que
  // Microsoft demande un consentement, obtenirJeton passe par une interaction.
  return obtenirJeton(sharepointTokenRequest, account)
}

// ─── Options de requête ───

export interface SharePointFetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT' | 'MERGE'
  body?: unknown
  headers?: Record<string, string>
  /**
   * Site path (ex: '/sites/OP_SCE'). La portion API (_api/...) doit être
   * fournie dans `apiPath`.
   */
  sitePath: string
  /**
   * Chemin de l'API SharePoint REST (ex: 'web/ensureUser').
   * Sera préfixé par `_api/`.
   */
  apiPath: string
}

/**
 * Wrapper principal pour appeler SharePoint REST API.
 *
 * Construit l'URL complète à partir du hostname + sitePath + apiPath.
 * Exemple :
 *   sharepointFetch({ sitePath: '/sites/OP_SCE', apiPath: 'web/ensureUser', ... })
 *   → POST https://sartorius.sharepoint.com/sites/OP_SCE/_api/web/ensureUser
 */
export function sharepointFetch<T = unknown>(
  options: SharePointFetchOptions,
): Promise<T> {
  // Comptée pour l'écran d'ouverture de session (cf. activiteReseau.ts).
  return suivreRequete(() => executerSharePoint<T>(options))
}

async function executerSharePoint<T>(options: SharePointFetchOptions): Promise<T> {
  const token = await getSharePointAccessToken()

  const url = `https://${SHAREPOINT_HOSTNAME}${options.sitePath}/_api/${options.apiPath}`

  // Format JSON verbose par défaut → SP REST renvoie { d: { ... } }
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json;odata=verbose',
    ...options.headers,
  }

  let body: BodyInit | undefined
  if (options.body !== undefined) {
    body = JSON.stringify(options.body)
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json;odata=verbose'
    }
  }

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    ...(body !== undefined ? { body } : {}),
  })

  // ─── Gestion des erreurs HTTP ───
  if (!response.ok) {
    let details: unknown
    try {
      details = await response.json()
    } catch {
      details = await response.text().catch(() => null)
    }

    switch (response.status) {
      case 401:
        throw new SharePointApiError(
          'Session SharePoint expirée — reconnexion requise',
          401,
          details,
        )
      case 403:
        throw new SharePointApiError(
          'Accès SharePoint refusé — permissions insuffisantes (scope AllSites.Write ?)',
          403,
          details,
        )
      case 404:
        throw new SharePointApiError('Ressource SharePoint introuvable', 404, details)
      case 429:
        throw new SharePointApiError(
          'Trop de requêtes SharePoint — réessayer dans un instant',
          429,
          details,
        )
      default:
        throw new SharePointApiError(
          `Erreur SharePoint REST (${response.status})`,
          response.status,
          details,
        )
    }
  }

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/json')) {
    const raw = (await response.json()) as { d?: T } | T
    // SP REST avec Accept verbose enveloppe dans { d: ... }
    if (raw && typeof raw === 'object' && 'd' in raw) {
      return (raw as { d: T }).d
    }
    return raw as T
  }

  return (await response.text()) as unknown as T
}

// ─── Helpers pratiques ───

export function sharepointGet<T>(
  sitePath: string,
  apiPath: string,
): Promise<T> {
  return sharepointFetch<T>({ sitePath, apiPath, method: 'GET' })
}

export function sharepointPost<T>(
  sitePath: string,
  apiPath: string,
  body: unknown,
): Promise<T> {
  return sharepointFetch<T>({ sitePath, apiPath, method: 'POST', body })
}
